import { capitalize } from "lodash";

import {
  PATIENT_PRODUCT_STATUSES,
  PATIENT_PRODUCT_STATUS_CANCELED,
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_EXCHANGED,
  PATIENT_PRODUCT_STATUS_ON_LOAN,
  PATIENT_PRODUCT_STATUS_RETURNED
} from "constants/patient-products";
import {
  ACCESSORIES_SUB_TYPES,
  BATTERY_COLORS,
  BATTERY_SUBTYPE,
  CHARGER_SUBTYPE,
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  HEARING_AID_TYPES,
  OTHER_SUBTYPE,
  RECEIVER_SUBTYPE,
  REMOTE_SUBTYPE,
  TECHNOLOGIES,
  TUBE_SUBTYPE
} from "constants/products";
import { joinValues } from "features/patients/sections/patient-hearing-aids/new-hearing-aid/new-hearing-aid-wizard/utils/new-hearing-aid-wizard.utils";
import { AssignedTag } from "services/types/assigned-tag.type";
import {
  type BatteryInventoryProductSpecification,
  type ChargerInventoryProductSpecification,
  type DomeInventoryProductSpecification,
  type EarmoldInventoryProductSpecification,
  type HearingAidInventoryProductSpecification,
  type InventoryProduct,
  type OtherInventoryProductSpecification,
  type ReceiverInventoryProductSpecification,
  type RemoteInventoryProductSpecification,
  type TubeInventoryProductSpecification
} from "services/types/inventory-products.type";
import {
  type BatterySpecification,
  type HearingAidMasterProductSpecification
} from "services/types/master-products.type";
import {
  LossDamageLinkedProduct,
  type PatientProduct,
  type PatientProductFilters,
  type PatientProductType
} from "services/types/patient-products.type";

import {
  INVENTORY_PRODUCT_STATUS_ON_DEMO,
  INVENTORY_PRODUCT_STATUS_ON_LOAN
} from "../constants/inventory-products";
import {
  ACCESSORY_PRODUCT_TYPE,
  COCHLEAR_IMPLANT_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SALE_QUOTE_STATUS_TYPE,
  SUPPLY_PRODUCT_TYPE
} from "../constants/sales";
import {
  formatBackendDateOnly,
  formatValidQueryParamUrlCommaSeparated,
  isBlank
} from "./common";

// A device that was returned/exchanged/canceled has its inventory_product cleared and its
// description stamped as `<display name>(#<inventory_product_id>)` by the backend's
// return_to_inventory (e.g. "Phonak Audéo P90(#12345)"). Such a device reads as an "Outside
// Purchase" even though it came from clinic stock. This distinguishes it from a genuine
// outside purchase (which also has no inventory link but no trailing `(#id)`), so we only
// offer the "Reconnect to Inventory" action on the former. (SN-18821)
const DISCONNECTED_DESCRIPTION_PATTERN = /\(#\d+\)\s*$/;

export function isDisconnectedFromInventory(patientProduct: {
  inventory_product?: unknown;
  inventory_product_id?: number | null;
  description?: string | null;
}): boolean {
  const hasInventoryLink =
    Boolean(patientProduct.inventory_product) ||
    Boolean(patientProduct.inventory_product_id);
  const { description } = patientProduct;
  return (
    !hasInventoryLink &&
    Boolean(description) &&
    DISCONNECTED_DESCRIPTION_PATTERN.test(description as string)
  );
}

export type ActivePatientProduct = {
  id: number;
  condition?: number | null;
  sale_id?: number | null;
  // Status of the linked sale — used to label quote devices ("Quote" vs "Invoice")
  // and offer the quote conversion action (SN-18215)
  sale_status?: number | null;
  sale_item?: number | null;
  is_migrated?: boolean;
  // The catalog product's category id, read live through inventory_product.
  product_category?: number | null;
  assigned_tags?: AssignedTag[];
  inventory_product_id: number | null;
  inventory_product?: InventoryProduct;
  product_type: PatientProductType;
  display_name: string;
  ear: "L" | "R";
  serial_number?: string;
  order_number?: string;
  status: string;
  status_code: number;
  statuses: { created_at: string; value: number }[];
  inventory_product_status: number | null;
  purchase_date: string;
  service_plan_expiration_date?: string;
  manufacturer_warranty?: string;
  lost_and_damage_warranty?: string;
  // Loss & Damage (SN-7365)
  loss_damage_used?: boolean;
  loss_damage_used_at?: string | null;
  loss_damage_used_at_display?: string | null;
  replaced_patient_product_detail?: LossDamageLinkedProduct | null;
  replacement_patient_product?: LossDamageLinkedProduct | null;
  extended_warranty_expiration_date?: string;
  due_date?: string;
  manufacturer?: string;
  model?: string;
  technology?: string;
  type?: string;
  gain?: string;
  description?: string;
  dome_type?: string;
  dome_size?: string;
  tube_size?: string;
  length?: number | string | null;
  name?: string;
  color?: string;
  battery?: string;
  manufacturer_instruction?: string;
  canal_length?: string;
  venting?: string;
  material?: string;
  options?: string;
  additional_notes?: string | null;
  quantity?: number;
  is_in_use?: boolean;
  is_outside_purchase?: boolean;
  loanable?: boolean;
  demoable?: boolean;
  delivered_at: string;
  delivered_at_display?: string | null;
};

export const isProductOfType =
  (productType: PatientProductType) => (patientProduct: PatientProduct) => {
    const { type, subtype } =
      patientProduct?.inventory_product?.product?.master_product ||
      patientProduct ||
      {};
    switch (productType) {
      case "hearing-aid":
        return type === 2 || type === undefined;
      case "cochlear-implant":
        return type === 6;
      case "receiver":
        return type === 3 && subtype === RECEIVER_SUBTYPE;
      case "earmold":
        return type === 3 && subtype === EAR_MOLD_SUBTYPE;
      case "dome":
        return type === 3 && subtype === DOME_SUBTYPE;
      case "tube":
        return type === 3 && subtype === TUBE_SUBTYPE;
      case "accessories":
        return (
          type === 3 &&
          [
            BATTERY_SUBTYPE,
            CHARGER_SUBTYPE,
            REMOTE_SUBTYPE,
            OTHER_SUBTYPE
          ].includes(Number(subtype))
        );

      default:
        return false;
    }
  };

export const getProductTypes = (
  productType: PatientProductType | undefined
): {
  type: number | null;
  subtype: [number, number, number, number] | [number] | [null];
} => {
  switch (productType) {
    case "hearing-aid":
      return { type: 2, subtype: [null] };
    case "receiver":
      return { type: 3, subtype: [RECEIVER_SUBTYPE] };
    case "tube":
      return { type: 3, subtype: [TUBE_SUBTYPE] };
    case "dome":
      return { type: 3, subtype: [DOME_SUBTYPE] };
    case "earmold":
      return { type: 3, subtype: [EAR_MOLD_SUBTYPE] };
    case "battery":
      return { type: 3, subtype: [BATTERY_SUBTYPE] };
    case "charger":
      return { type: 3, subtype: [CHARGER_SUBTYPE] };
    case "remote":
      return { type: 3, subtype: [REMOTE_SUBTYPE] };
    case "other":
      return { type: 3, subtype: [OTHER_SUBTYPE] };
    case "accessories":
      return {
        type: 3,
        subtype: [
          BATTERY_SUBTYPE,
          CHARGER_SUBTYPE,
          REMOTE_SUBTYPE,
          OTHER_SUBTYPE
        ]
      };
    case "cochlear-implant": {
      return { type: 6, subtype: [null] };
    }
    default:
      return { type: null, subtype: [null] };
  }
};

export function getActiveHearingAid(
  hearingAidsProducts: PatientProduct[],
  ear: "L" | "R" | ""
) {
  const earHearingAids = hearingAidsProducts.filter(
    (patientProduct) => patientProduct.ear === ear
  );
  return earHearingAids.map((ha) => convertToDisplay(ha));
}

export function convertToDisplay(
  patientProduct: PatientProduct
): Partial<ActivePatientProduct> {
  const display = convertToDisplayInner(patientProduct);
  if (patientProduct) {
    display.is_migrated = Boolean(patientProduct.is_migrated);
    display.product_category =
      patientProduct.inventory_product?.product?.category ?? null;
  }
  return display;
}

function convertToDisplayInner(
  patientProduct: PatientProduct
): Partial<ActivePatientProduct> {
  if (!patientProduct) {
    return {};
  }

  const manufacturer_warranty_expiration_date =
    patientProduct?.manufacturer_warranty_expiration_date ??
    patientProduct?.inventory_product?.manufacturer_warranty_expiration_date;

  const patientProductManufacturerWarranty =
    manufacturer_warranty_expiration_date
      ? formatBackendDateOnly(
          manufacturer_warranty_expiration_date,
          "MMM dd, yyyy"
        )
      : "";

  const service_plan_expiration_date =
    patientProduct?.service_plan_expiration_date
      ? formatBackendDateOnly(
          patientProduct?.service_plan_expiration_date,
          "MMM dd, yyyy"
        )
      : "";

  const loss_damage_warranty_expiration_date =
    patientProduct?.loss_damage_warranty_expiration_date ??
    patientProduct?.inventory_product?.loss_damage_warranty_expiration_date;

  const patientProductLostAndDamageWarranty =
    loss_damage_warranty_expiration_date
      ? formatBackendDateOnly(
          loss_damage_warranty_expiration_date,
          "MMM dd, yyyy"
        )
      : "";

  const extended_warranty_expiration_date =
    patientProduct?.extended_warranty_expiration_date;

  const patientProductExtendedWarrantyExpirationDate =
    extended_warranty_expiration_date
      ? formatBackendDateOnly(extended_warranty_expiration_date, "MMM dd, yyyy")
      : "";

  const patientProductPurchaseDate = patientProduct.purchase_date
    ? formatBackendDateOnly(patientProduct.purchase_date, "MMM dd, yyyy")
    : "";

  const patientProductDeliveredAt = patientProduct.delivered_at
    ? formatBackendDateOnly(patientProduct.delivered_at, "MMM dd, yyyy")
    : "";

  const patientProductDueDate = patientProduct.due_date
    ? formatBackendDateOnly(patientProduct.due_date, "MMM dd, yyyy")
    : "";
  const patientProductSerialNumber = patientProduct.serial_number || "";

  if (!patientProduct.inventory_product) {
    return {
      id: patientProduct.id,
      sale_id: patientProduct.sale?.id,
      sale_status: patientProduct.sale?.status,
      sale_item: patientProduct.sale_item,
      status_code: patientProduct.status,
      loss_damage_used: patientProduct.loss_damage_used,
      loss_damage_used_at: patientProduct.loss_damage_used_at,
      loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
      replaced_patient_product_detail:
        patientProduct.replaced_patient_product_detail,
      replacement_patient_product: patientProduct.replacement_patient_product,
      statuses: patientProduct.statuses,
      assigned_tags: patientProduct.assigned_tags,
      status: PATIENT_PRODUCT_STATUSES.getFromCode(patientProduct.status),
      inventory_product_id: null,
      inventory_product_status: null,
      ear: patientProduct.ear,
      serial_number: patientProductSerialNumber,
      description: patientProduct.description,
      display_name: patientProduct.description,
      service_plan_expiration_date,
      manufacturer_warranty: patientProductManufacturerWarranty,
      lost_and_damage_warranty: patientProductLostAndDamageWarranty,
      extended_warranty_expiration_date:
        patientProductExtendedWarrantyExpirationDate,
      purchase_date: patientProductPurchaseDate,
      due_date: patientProductDueDate,
      product_type: getProductType({
        type: patientProduct.type,
        subtype: patientProduct.subtype
      }),
      is_in_use: patientProduct.is_in_use,
      is_outside_purchase: patientProduct.is_outside_purchase,
      loanable: false,
      demoable: false,
      delivered_at_display: patientProduct.delivered_at_display,
      delivered_at: patientProductDeliveredAt,
      additional_notes: patientProduct.notes
    };
  }
  const { product, serial_number } = patientProduct.inventory_product;
  const { master_product, display_name } = product!;

  const effectiveSubtype =
    master_product.type === 2 || master_product.type === 6
      ? null
      : master_product.subtype;
  switch (effectiveSubtype?.toString() || "null") {
    case "null": {
      const { ear, order_item, status, inventory_product, sale } =
        patientProduct;
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};

      const purchase_date = sale?.service_date || patientProduct.purchase_date;
      const { tracking_number: order_number } = order_item || {};
      const { model, specification } = master_product;
      const manufacturer = master_product.manufacturer?.name ?? "";
      const { technology, type } =
        (specification as HearingAidMasterProductSpecification) || {};
      const { color, battery, manufacturer_instruction, additional_notes } =
        (inventory_product?.specification as HearingAidInventoryProductSpecification) ||
        {};
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        assigned_tags: patientProduct.assigned_tags,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        product_type: "hearing-aid",
        display_name: display_name || "",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        manufacturer,
        model,
        technology: TECHNOLOGIES.getFromCode(technology),
        type: HEARING_AID_TYPES.getFromCode(type),
        manufacturer_instruction,
        color,
        battery,
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "1": {
      const specification = patientProduct.inventory_product
        .specification as ReceiverInventoryProductSpecification;
      const { ear, order_item, status, inventory_product, sale } =
        patientProduct;
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const { model } = master_product;
      const manufacturer = master_product.manufacturer?.name ?? "";
      const { tracking_number: order_number } = order_item || {};
      const { gain, length, additional_notes } = specification || {};
      return {
        product_type: "receiver",
        display_name: display_name || "",
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        assigned_tags: patientProduct.assigned_tags,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        manufacturer,
        gain: gain?.toString(),
        length,
        model,
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "2": {
      const { master_product } = patientProduct.inventory_product.product!;
      const { ear, order_item, status, inventory_product, sale } =
        patientProduct;
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product;
      const specification =
        inventory_product.specification as EarmoldInventoryProductSpecification;
      const { canal_length, venting, material, options, additional_notes } =
        specification || {};
      const purchase_date = sale?.service_date;
      const { tracking_number: order_number } = order_item || {};
      const { description: type } = master_product;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        assigned_tags: patientProduct.assigned_tags,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        display_name: display_name || "",
        product_type: "earmold",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        type,
        canal_length,
        venting: venting || "",
        material: material || "",
        options: options || "",
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "3": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const { tracking_number: order_number } = order_item || {};
      const tubeSpecification =
        (inventory_product.specification as TubeInventoryProductSpecification) ||
        {};
      const { additional_notes, size: tube_size } = tubeSpecification;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        assigned_tags: patientProduct.assigned_tags,
        sale_item: patientProduct.sale_item,
        product_type: "tube",
        display_name: display_name || "",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        tube_size: tube_size || "",
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "4": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const domeSpecification =
        (inventory_product.specification as DomeInventoryProductSpecification) ||
        {};
      const {
        additional_notes,
        size: dome_size,
        type: dome_type
      } = domeSpecification;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        product_type: "dome",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        statuses: patientProduct.statuses,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        dome_size: dome_size || "",
        dome_type: dome_type || "",
        additional_notes,
        condition,
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "5": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;
      const { specification } = master_product;
      const { color } = (specification as BatterySpecification) || {};
      const spec =
        (inventory_product.specification as BatteryInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "battery",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        color: BATTERY_COLORS.getFromCode(color),
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "6": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;

      const spec =
        (inventory_product.specification as ChargerInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        assigned_tags: patientProduct.assigned_tags,
        sale_item: patientProduct.sale_item,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "charger",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "9": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;

      const spec =
        (inventory_product.specification as RemoteInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: patientProduct.sale?.id,
        sale_status: patientProduct.sale?.status,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "remote",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    case "999": {
      const { ear, status, inventory_product, order_item, sale } =
        patientProduct || {};
      const { tracking_number: order_number } = order_item || {};
      const {
        condition,
        loanable,
        demoable,
        status: inventory_product_status
      } = inventory_product || {};
      const purchase_date = sale?.service_date;

      const spec =
        (inventory_product.specification as OtherInventoryProductSpecification) ||
        {};
      const { additional_notes } = spec;
      return {
        id: patientProduct.id,
        sale_id: sale?.id,
        sale_status: sale?.status,
        inventory_product_id: patientProduct.inventory_product?.id || null,
        inventory_product: patientProduct.inventory_product,
        sale_item: patientProduct.sale_item,
        assigned_tags: patientProduct.assigned_tags,
        display_name: display_name || "",
        description: master_product.description,
        product_type: "other",
        ear,
        order_number,
        status: PATIENT_PRODUCT_STATUSES.getFromCode(status),
        statuses: patientProduct.statuses,
        status_code: patientProduct.status,
        loss_damage_used: patientProduct.loss_damage_used,
        loss_damage_used_at: patientProduct.loss_damage_used_at,
        loss_damage_used_at_display: patientProduct.loss_damage_used_at_display,
        replaced_patient_product_detail:
          patientProduct.replaced_patient_product_detail,
        replacement_patient_product: patientProduct.replacement_patient_product,
        inventory_product_status,
        serial_number: patientProductSerialNumber || serial_number,
        purchase_date: purchase_date
          ? formatBackendDateOnly(purchase_date, "MMM dd, yyyy")
          : patientProductPurchaseDate || "",
        service_plan_expiration_date,
        manufacturer_warranty: patientProductManufacturerWarranty,
        lost_and_damage_warranty: patientProductLostAndDamageWarranty,
        extended_warranty_expiration_date:
          patientProductExtendedWarrantyExpirationDate,
        additional_notes,
        condition,
        quantity: getQuantity(sale, inventory_product.product.id),
        due_date: patientProductDueDate,
        is_in_use: patientProduct.is_in_use,
        loanable,
        demoable,
        delivered_at_display: patientProduct.delivered_at_display,
        delivered_at: patientProductDeliveredAt
      };
    }
    default:
      return {};
  }
}

export const getQuantity = (sale: any, inventoryProductId: number) => {
  return (
    sale?.items?.find((item: any) => item?.product?.id === inventoryProductId)
      ?.quantity || 0
  );
};

// is_active tracks whether a device is still part of the patient's active
// roster, not whether it's been delivered — Requested/Ordered/Shipped/etc.
// devices are legitimately Active. The one state that must stay blocked is
// reactivating a device whose status shows it's no longer with the patient
// (Returned/Canceled/Exchanged): that corrupts the next return cycle's stock
// routing (see DEV-1711 / ENG-1625).
const PATIENT_PRODUCT_STATUSES_BLOCKING_ACTIVATION: number[] = [
  PATIENT_PRODUCT_STATUS_RETURNED,
  PATIENT_PRODUCT_STATUS_CANCELED,
  PATIENT_PRODUCT_STATUS_EXCHANGED
];

export const canActivatePatientProduct = (status: number | undefined | null) =>
  status == null ||
  !PATIENT_PRODUCT_STATUSES_BLOCKING_ACTIVATION.includes(status);

// The edit form always sends its is_active / is_in_use, and the backend refuses
// is_active: true alongside a blocking status. The form's own effect clears both
// when its status field changes, but the status review window saves its status as
// an override without writing it into the form first, so the override has to take
// the device out of use itself (ENG-2635).
export const takeOutOfUseForBlockedStatus = <
  T extends { status?: number | null; is_active?: boolean; is_in_use?: boolean }
>(
  overrides: T
): T =>
  overrides.status != null && !canActivatePatientProduct(overrides.status)
    ? { ...overrides, is_active: false, is_in_use: false }
    : overrides;

// `is_in_use` is only ever set true in two places: when a device is delivered to the patient
// (suno-backend `deliver_to_patient`) and when a loaner is issued (`loaner_strategy`). So at
// any OTHER status the flag has simply never been true — nobody has expressed a preference —
// and reading it as "the user unticked Show in patient header" is wrong. These are the two
// statuses where the flag is a genuine choice, and the only ones where unticking it hides the
// device (ENG-1789).
const PATIENT_PRODUCT_WITH_PATIENT_STATUSES: number[] = [
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_ON_LOAN
];

// A device that has left the patient for good. `return_to_inventory` sets `is_active = False`
// on all three, so the backend's ear-slot lookup never returns one and the products list is
// fetched with `is_active: true` — this is belt and braces, and keeps "Returned by Patient"
// from ever being rendered as though the patient still had the device.
const PATIENT_PRODUCT_GONE_STATUSES: number[] = [
  PATIENT_PRODUCT_STATUS_RETURNED,
  PATIENT_PRODUCT_STATUS_EXCHANGED,
  PATIENT_PRODUCT_STATUS_CANCELED
];

export type PatientHeaderDeviceState = {
  isInUse?: boolean | null;
  status?: number | null;
  // Status of the device's SALE, not the device. A device on an open quote has been priced
  // and nothing more — see isOnOpenQuote below.
  saleStatus?: number | null;
  // Whether the patient has ever physically had this device. Only used to decide the age
  // line; visibility never reads it.
  wasDelivered?: boolean;
};

const isGoneFromPatient = (status?: number | null) =>
  status != null && PATIENT_PRODUCT_GONE_STATUSES.includes(status);

// A device on a still-open quote is an estimate: nothing was ordered, nothing left stock, and
// the patient has agreed to nothing. `purchase_strategy.py` parks it at REQUESTED ("To be
// Ordered") with `is_in_use` false, which is indistinguishable from a real order by status
// alone — so without this the header would present a price quote as a device the patient has.
//
// The backend already excludes these from `left_ear_hearing_aid` / `right_ear_hearing_aid`
// (`Patient._get_ear_device`, SN-18215), but NOT from `/patient-products/`, which is where the
// cochlear-implant slot and the hover tooltip read from — deliberately, because the Hearing
// Aids tab lists quote devices and labels them "Quote". So the rule has to be re-stated here.
export const isOnOpenQuote = (saleStatus?: number | null) =>
  saleStatus != null && Number(saleStatus) === SALE_QUOTE_STATUS_TYPE;

export const isPatientProductWithPatient = (status?: number | null) =>
  status != null && PATIENT_PRODUCT_WITH_PATIENT_STATUSES.includes(status);

// THE rule for whether a device occupies a patient-header ear slot, and the only place it is
// written.
//
// ENG-1789 made an unticked device disappear entirely, on the premise that `is_in_use` is the
// user's "Show in patient header" checkbox. That premise only holds once a device has reached
// the patient. Everywhere else the backend writes the flag itself — the repair request clears
// it (`repair_strategy.py`), and it was never set on a device that is still To be Ordered /
// Order Submitted / Shipped / Accepted by Clinic / Ready for Delivery — so hiding those made
// the header claim "No active hearing aid" over devices the practice was actively handling.
// Four separate customer reports, one rule: respect the checkbox for a device that is with the
// patient, and show every other active device with the status it already carries.
export const isShownInPatientHeader = ({
  isInUse,
  status,
  saleStatus
}: PatientHeaderDeviceState) => {
  if (isOnOpenQuote(saleStatus)) return false;
  if (isGoneFromPatient(status)) return false;
  return Boolean(isInUse) || !isPatientProductWithPatient(status);
};

// Whether the age line is meaningful. "Age" is how long the PATIENT has had the device, so it
// needs a delivery, not a purchase: a device still on order usually has a `purchase_date` (its
// sale's service date) and no delivery, and reading that as an age printed "0 days" beside a
// device nobody had yet. A device out for repair is the opposite case and keeps its age — the
// patient has owned it for years, it is away for a week.
export const hasPatientHadDevice = ({
  status,
  wasDelivered
}: PatientHeaderDeviceState) =>
  Boolean(wasDelivered) || isPatientProductWithPatient(status);

// Whether the header / drawer should print the device's status beside its name. A device that
// is with the patient needs no explaining (and an On Loan one already gets `ConditionIndicator`,
// so a chip there would just say the same thing twice); anything else is in the slot precisely
// because of its status, and saying so is what stops it reading as an ordinary worn device —
// the half-explained render ENG-1789 was raised about.
export const shouldShowPatientDeviceStatus = (status?: number | null) =>
  status != null &&
  !isPatientProductWithPatient(status) &&
  !isGoneFromPatient(status);

// Picks the one device that fills an ear slot from a list of candidates. In-use FIRST, always:
// the backend allows only one `is_in_use` device per ear (un_use_same_type_patient_products)
// and orders the slot by `-is_in_use` (Patient._get_ear_device), so a plain `.find` over a
// predicate that also matches devices away from the patient would let one at the manufacturer —
// or one still on order — outrank the one being worn. List order here is by purchase date, not
// by the flag.
export const findPatientHeaderDevice = <
  T extends { is_in_use?: boolean; status_code?: number | null }
>(
  products: T[] | undefined,
  toState: (product: T) => PatientHeaderDeviceState
): T | undefined =>
  products?.find(
    (product) => product.is_in_use && isShownInPatientHeader(toState(product))
  ) ?? products?.find((product) => isShownInPatientHeader(toState(product)));

// The `ActivePatientProduct` half of the two shapes above, so its field names are spelled out
// once rather than at each call site. Note the code lives in `status_code` here (`status` holds
// the LABEL), and that `delivered_at` is a pre-formatted string, blank when never delivered.
export const activeProductHeaderState = (
  product: Pick<
    ActivePatientProduct,
    "is_in_use" | "status_code" | "sale_status" | "delivered_at"
  >
): PatientHeaderDeviceState => ({
  isInUse: product.is_in_use,
  status: product.status_code,
  saleStatus: product.sale_status,
  wasDelivered: !isBlank(product.delivered_at)
});

export const PATIENT_PRODUCT_ACTIVATION_BLOCKED_TOOLTIP =
  "This device is Returned, Canceled, or Exchanged and can't be reactivated here. To re-issue it, create a new sale or loan.";

export const isProductConditional = ({
  status,
  inventoryProductStatus,
  isPatientProduct = false,
  isLoanable = false,
  isDemoable = false
}: {
  status?: string | number;
  inventoryProductStatus?: number | null;
  isPatientProduct?: boolean;
  isLoanable?: boolean;
  isDemoable?: boolean;
}) => {
  if (isPatientProduct) {
    let patientProductCondition = false;
    if (typeof status === "string") {
      patientProductCondition = [
        PATIENT_PRODUCT_STATUSES.getFromCode(PATIENT_PRODUCT_STATUS_ON_LOAN)
      ].includes(status);
    } else if (typeof status === "number") {
      patientProductCondition = [PATIENT_PRODUCT_STATUS_ON_LOAN].includes(
        status
      );
    }
    return (
      patientProductCondition ||
      [
        INVENTORY_PRODUCT_STATUS_ON_LOAN,
        INVENTORY_PRODUCT_STATUS_ON_DEMO
      ].includes(inventoryProductStatus || -1)
    );
  } else {
    return isLoanable || isDemoable;
  }
};

export function getCurrentHearingAid(
  hearingAid: Omit<PatientProduct, "patient"> | null | undefined
): string {
  if (!hearingAid) return "No active hearing aid";
  const { inventory_product, description } = hearingAid;
  if (inventory_product === null || inventory_product === undefined) {
    return description;
  }
  const { product } = inventory_product;
  return joinValues([product?.display_name]);
}

export function getCurrentHearingAids(
  leftHearingAid: Omit<PatientProduct, "patient"> | null | undefined,
  rightHearingAid: Omit<PatientProduct, "patient"> | null | undefined
): string {
  if (!leftHearingAid && !rightHearingAid) {
    return "No active hearing aids.";
  }
  const activeHearingAids: string[] = [];
  if (leftHearingAid) {
    activeHearingAids.push("L: " + getCurrentHearingAid(leftHearingAid));
  }
  if (rightHearingAid) {
    activeHearingAids.push("R: " + getCurrentHearingAid(rightHearingAid));
  }
  return activeHearingAids.join(", ");
}

export function getCurrentDevicesList(params: {
  leftHearingAid: Omit<PatientProduct, "patient"> | null | undefined;
  rightHearingAid: Omit<PatientProduct, "patient"> | null | undefined;
  leftCochlearImplant: Omit<PatientProduct, "patient"> | null | undefined;
  rightCochlearImplant: Omit<PatientProduct, "patient"> | null | undefined;
}): string[] {
  const {
    leftHearingAid,
    rightHearingAid,
    leftCochlearImplant,
    rightCochlearImplant
  } = params;
  const parts: string[] = [];

  if (leftHearingAid) {
    parts.push("L: " + getCurrentHearingAid(leftHearingAid) + " (Hearing Aid)");
  }
  if (leftCochlearImplant) {
    parts.push(
      "L: " + getCurrentHearingAid(leftCochlearImplant) + " (Cochlear Implant)"
    );
  }
  if (rightHearingAid) {
    parts.push(
      "R: " + getCurrentHearingAid(rightHearingAid) + " (Hearing Aid)"
    );
  }
  if (rightCochlearImplant) {
    parts.push(
      "R: " + getCurrentHearingAid(rightCochlearImplant) + " (Cochlear Implant)"
    );
  }
  if (parts.length === 0) {
    parts.push("No active devices.");
  }
  return parts;
}

export function getCurrentDevices(
  leftHearingAid: Omit<PatientProduct, "patient"> | null | undefined,
  rightHearingAid: Omit<PatientProduct, "patient"> | null | undefined,
  leftCochlearImplant: Omit<PatientProduct, "patient"> | null | undefined,
  rightCochlearImplant: Omit<PatientProduct, "patient"> | null | undefined
): string {
  const parts: string[] = getCurrentDevicesList({
    leftHearingAid,
    rightHearingAid,
    leftCochlearImplant,
    rightCochlearImplant
  });
  return parts.join(", ");
}

export function getProductType({
  type,
  subtype
}: {
  type: number | null | undefined;
  subtype: number | null | undefined;
}): PatientProductType | undefined {
  if (type?.toString() === ACCESSORY_PRODUCT_TYPE.toString()) {
    switch (subtype?.toString()) {
      case RECEIVER_SUBTYPE.toString():
        return "receiver";
      case EAR_MOLD_SUBTYPE.toString():
        return "earmold";
      case DOME_SUBTYPE.toString():
        return "dome";
      case TUBE_SUBTYPE.toString():
        return "tube";
      case BATTERY_SUBTYPE.toString():
        return "battery";
      case CHARGER_SUBTYPE.toString():
        return "charger";
      case REMOTE_SUBTYPE.toString():
        return "remote";
      case OTHER_SUBTYPE.toString():
        return "other";
      default:
        return "other";
    }
  } else if (type?.toString() === HEARING_AID_PRODUCT_TYPE.toString()) {
    return "hearing-aid";
  } else if (type?.toString() === SUPPLY_PRODUCT_TYPE.toString()) {
    return "supply";
  } else if (type?.toString() === COCHLEAR_IMPLANT_PRODUCT_TYPE.toString()) {
    return "cochlear-implant";
  } else {
    return undefined;
  }
}

export function productTypeName(productType: string) {
  if (!productType) return "";
  if (productType === "hearing-aid") return "Hearing Aid";
  if (productType === "cochlear-implant") return "Cochlear Implant";

  return capitalize(productType);
}

// Accessories are labelled by subtype ("Dome", "Receiver"); every other type is
// labelled by the type itself. Feeds the clinic stock list, the patient products
// list, the inventory-product autocomplete and the "Check-in new …" modal title.
//
// Written as an allowlist of Hearing Aid / Supply, it silently dropped Cochlear
// Implant and printed a bare "N/A" on every one of those surfaces. Inverted so any
// type present in `PRODUCT_TYPES` resolves and only a missing/unknown type falls
// back to "N/A" — a type added to the enum now works here without a second edit.
export function getProductDisplayType({
  type,
  subtype
}: {
  type?: number | string | null;
  subtype?: number | string | null;
}) {
  if (type === null || type === undefined || type === "") {
    return "N/A";
  }

  if (type.toString() === ACCESSORY_PRODUCT_TYPE.toString()) {
    return subtype
      ? ACCESSORIES_SUB_TYPES.getFromCode(subtype)
      : PRODUCT_TYPES.getFromCode(type);
  }

  return PRODUCT_TYPES.getFromCode(type) || "N/A";
}

export const formatPatientProductFilters = ({
  patientId,
  query,
  baseFilters,
  ...filters
}: PatientProductFilters) => {
  return {
    ...baseFilters,
    ...filters,
    patient: patientId
  };
};

export const getPatientProductsEndpointWithQueryParameters = (
  baseUrl: string,
  { query = "{*}", search, ...filters }: PatientProductFilters
) => {
  return `${baseUrl}?${formatValidQueryParamUrlCommaSeparated(
    formatPatientProductFilters({
      ...filters,
      search: search && encodeURIComponent(search)
    })
  )}&query=${query}`;
};
